import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import { PageLoading } from "../../shared/components/PageLoading";
import type { TranslationKey } from "../../shared/i18n/translations";
import {
  changeFinanceStatus,
  getFinance,
  postFinanceEntry,
  reverseFinanceEntry,
  saveFinance,
  type FinanceRecord,
  type FinanceSnapshot,
} from "./financeApi";
import { ClientRecordListControls } from "../../shared/components/ClientRecordListControls";
import { useClientRecordList } from "../../shared/components/useClientRecordList";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { AppModal } from "../../shared/components/AppModal";

const k = (value: string) => value as TranslationKey;
export type FinanceSection =
  | "overview"
  | "accounts"
  | "years"
  | "journals"
  | "entries"
  | "taxes"
  | "treasury"
  | "banks"
  | "sii"
  | "reports";
type EntryLine = {
  accountId: string;
  description: string;
  debit: number;
  credit: number;
  taxCode: string;
};
const blankLine = (): EntryLine => ({
  accountId: "",
  description: "",
  debit: 0,
  credit: 0,
  taxCode: "",
});
const source: Record<FinanceSection, keyof FinanceSnapshot | ""> = {
  overview: "",
  accounts: "accounts",
  years: "fiscalYears",
  journals: "journals",
  entries: "entries",
  taxes: "taxCodes",
  treasury: "treasuryTransactions",
  banks: "bankAccounts",
  sii: "",
  reports: "",
};
const entities: Record<FinanceSection, string> = {
  overview: "",
  accounts: "accounts",
  years: "fiscal-years",
  journals: "journals",
  entries: "entries",
  taxes: "tax-codes",
  treasury: "treasury",
  banks: "bank-accounts",
  sii: "",
  reports: "",
};

export function FinancePage({
  section = "overview",
}: {
  section?: FinanceSection;
}) {
  const { user } = useAuth(),
    { t, formatNumber } = useLocale(),
    [data, setData] = useState<FinanceSnapshot>(),
    [error, setError] = useState<string>(),
    [editing, setEditing] = useState<FinanceRecord | null | undefined>();
  const load = async () => {
    try {
      setData(await getFinance());
      setError(undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "finance.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, [user?.activeCompanyId]);
  const records = (
    source[section] && data && Array.isArray(data[source[section]])
      ? data[source[section]]
      : []
  ) as FinanceRecord[];
  const list = useClientRecordList(records, `finance:${section}`);
  if (!data)
    return (
      <PageLoading
        label={t(k("finance.loading"))}
        error={error ? t(k(error)) : null}
      />
    );
  if (editing !== undefined) {
    const recordIndex = editing
      ? list.rows.findIndex((record) => record.id === editing.id)
      : -1;
    return (
      <FinanceEditor
        section={section}
        item={editing}
        data={data}
        cancel={() => setEditing(undefined)}
        saved={async () => {
          setEditing(undefined);
          await load();
        }}
        onPrevious={
          recordIndex > 0 ? () => setEditing(list.rows[recordIndex - 1]) : null
        }
        onNext={
          recordIndex >= 0 && recordIndex < list.rows.length - 1
            ? () => setEditing(list.rows[recordIndex + 1])
            : null
        }
      />
    );
  }
  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t(k("finance.eyebrow"))}</span>
          <h1>{t(k("finance.title"))}</h1>
          <p>
            {data.companyName} · {t(k("finance.subtitle"))}
          </p>
        </div>
      </header>
      {section === "overview" ? (
        <FinanceOverview data={data} />
      ) : section === "sii" ? (
        <FinanceSii data={data} />
      ) : section === "reports" ? (
        <FinanceReports data={data} />
      ) : (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t(k(`finance.${section}`))}</h2>
            {user?.permissions.finance === "Write" && (
                <button
                  className="primary-button"
                  onClick={() => setEditing(null)}
                >
                  + {t("administration.add")}
                </button>
              )}
          </div>
          {section === "years" && (
            <PeriodSummary
              data={data}
              reload={load}
              writable={user?.permissions.finance === "Write"}
            />
          )}
          <ClientRecordListControls list={list} />
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  <SortableHeader
                    label={t("aplPanel.code")}
                    active={list.sort === "code"}
                    direction={list.direction}
                    onClick={() => list.changeSort("code")}
                  />
                  <SortableHeader
                    label={t("aplPanel.name")}
                    active={list.sort === "name"}
                    direction={list.direction}
                    onClick={() => list.changeSort("name")}
                  />
                  <SortableHeader
                    label={t("planning.status")}
                    active={list.sort === "status"}
                    direction={list.direction}
                    onClick={() => list.changeSort("status")}
                  />
                  <SortableHeader
                    label={t(k("commercial.total"))}
                    active={list.sort === "amount"}
                    direction={list.direction}
                    onClick={() => list.changeSort("amount")}
                  />
                  <th>
                    <span className="visually-hidden">
                      {t("administration.actions")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <button
                        className="record-code-link"
                        onClick={() => setEditing(record)}
                      >
                        {String(
                          record.code ?? record.number ?? record.year ?? "—",
                        )}
                      </button>
                    </td>
                    <td>{String(record.name ?? record.description ?? "—")}</td>
                    <td>
                      {String(
                        record.status ??
                          (record.isClosed
                            ? t(k("finance.closed"))
                            : record.isActive === false
                              ? t("administration.inactive")
                              : t("administration.active")),
                      )}
                    </td>
                    <td>
                      {typeof (record.amount ?? record.debitTotal) === "number"
                        ? formatNumber(
                            Number(record.amount ?? record.debitTotal),
                          )
                        : "—"}
                    </td>
                    <td>
                      {user?.permissions.finance === "Write" && (
                        <FinanceActions
                          section={section}
                          record={record}
                          reload={load}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!list.rows.length && (
            <p className="empty-state">{t(k("finance.noResults"))}</p>
          )}
        </section>
      )}
    </main>
  );
}

function FinanceOverview({ data }: { data: FinanceSnapshot }) {
  const { t, formatNumber } = useLocale();
  return (
    <>
      <section className="maintenance-kpis quality-kpis">
        {Object.entries(data.overview).map(([name, value]) => (
          <article
            className="indicator-card neutral maintenance-kpi"
            key={name}
          >
            <span className="indicator-label">
              {t(k(`finance.kpi.${name}`))}
            </span>
            <div className="indicator-value">{formatNumber(value)}</div>
          </article>
        ))}
      </section>
      <section className="capacity-overview-grid">
        {(
          [
            "accounts",
            "years",
            "journals",
            "entries",
            "taxes",
            "treasury",
            "banks",
            "sii",
            "reports",
          ] as FinanceSection[]
        ).map((item) => (
          <Link
            className="capacity-overview-card"
            to={`/app/finanzas/${route(item)}`}
            key={item}
          >
            <h2>{t(k(`finance.${item}`))}</h2>
            <p>{t(k(`finance.${item}Description`))}</p>
            <span className="capacity-overview-link">
              {t("capacity.openSection")} →
            </span>
          </Link>
        ))}
      </section>
    </>
  );
}
function FinanceActions({
  section,
  record,
  reload,
}: {
  section: FinanceSection;
  record: FinanceRecord;
  reload: () => Promise<void>;
}) {
  const { t } = useLocale();
  const action = async (entity: string, status: string) => {
    await changeFinanceStatus(entity, record.id, status);
    await reload();
  };
  if (section === "entries") {
    if (record.status === "Draft")
      return (
        <button
          className="secondary-button"
          onClick={async () => {
            await postFinanceEntry(record.id);
            await reload();
          }}
        >
          {t(k("finance.post"))}
        </button>
      );
    if (record.status === "Posted")
      return (
        <button
          className="secondary-button"
          onClick={async () => {
            await reverseFinanceEntry(record.id);
            await reload();
          }}
        >
          {t(k("finance.reverse"))}
        </button>
      );
  }
  if (section === "years")
    return (
      <button
        className="secondary-button"
        onClick={() =>
          void action("fiscal-years", record.isClosed ? "Open" : "Closed")
        }
      >
        {t(k(record.isClosed ? "finance.reopen" : "finance.close"))}
      </button>
    );
  if (section === "treasury") {
    if (record.status === "Pending")
      return (
        <>
          <button
            className="secondary-button"
            onClick={() => void action("treasury", "Settled")}
          >
            {t(k("finance.settle"))}
          </button>
          <button
            className="danger-button"
            onClick={() => void action("treasury", "Cancelled")}
          >
            {t(k("finance.cancel"))}
          </button>
        </>
      );
    if (record.status === "Settled")
      return (
        <button
          className="secondary-button"
          onClick={() => void action("treasury", "Reconciled")}
        >
          {t(k("finance.reconcile"))}
        </button>
      );
  }
  return null;
}
function PeriodSummary({
  data,
  reload,
  writable,
}: {
  data: FinanceSnapshot;
  reload: () => Promise<void>;
  writable: boolean;
}) {
  const { t } = useLocale();
  return (
    <div className="administration-table-wrap">
      <table className="data-table">
        <caption>{t(k("finance.periods"))}</caption>
        <thead>
          <tr>
            <th>{t(k("finance.periodNumber"))}</th>
            <th>{t("aplPanel.name")}</th>
            <th>{t(k("finance.startsOn"))}</th>
            <th>{t(k("finance.endsOn"))}</th>
            <th>{t("planning.status")}</th>
            <th>
              <span className="visually-hidden">
                {t("administration.actions")}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.periods.map((period) => (
            <tr key={period.id}>
              <td>{String(period.number)}</td>
              <td>{String(period.name)}</td>
              <td>{String(period.startsOn)}</td>
              <td>{String(period.endsOn)}</td>
              <td>{t(k(`finance.status.${String(period.status)}`))}</td>
              <td>
                {writable && (
                  <button
                    className="secondary-button"
                    onClick={async () => {
                      await changeFinanceStatus(
                        "periods",
                        period.id,
                        period.status === "Open" ? "Closed" : "Open",
                      );
                      await reload();
                    }}
                  >
                    {t(
                      k(
                        period.status === "Open"
                          ? "finance.close"
                          : "finance.reopen",
                      ),
                    )}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinanceSii({ data }: { data: FinanceSnapshot }) {
  const { t, formatNumber } = useLocale();
  return (
    <section className="data-panel administration-panel">
      <div className="section-heading">
        <div>
          <h2>{t(k("finance.sii"))}</h2>
          <p>{t(k("finance.siiDescription"))}</p>
        </div>
      </div>
      <div className="administration-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t(k("finance.book"))}</th>
              <th>{t("quality.number")}</th>
              <th>{t(k("finance.taxIdentifier"))}</th>
              <th>{t(k("finance.entryDate"))}</th>
              <th>{t(k("commercial.total"))}</th>
              <th>{t("planning.status")}</th>
            </tr>
          </thead>
          <tbody>
            {data.siiRecords.map((record) => (
              <tr key={`${record.book}-${record.documentId}`}>
                <td>{t(k(`finance.book.${String(record.book)}`))}</td>
                <td>{String(record.number)}</td>
                <td>{String(record.partyTaxIdentifier || "—")}</td>
                <td>{String(record.issueDate)}</td>
                <td>{formatNumber(Number(record.totalAmount))}</td>
                <td>{t(k(`finance.siiStatus.${String(record.status)}`))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.siiRecords.length && (
        <p className="empty-state">{t(k("finance.noResults"))}</p>
      )}
    </section>
  );
}

function FinanceReports({ data }: { data: FinanceSnapshot }) {
  const { t, formatNumber } = useLocale();
  return (
    <div className="quality-section-stack">
      <StatementTable
        title={t(k("finance.balanceSheet"))}
        rows={data.balanceSheet}
      />
      <StatementTable
        title={t(k("finance.profitAndLoss"))}
        rows={data.profitAndLoss}
      />
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("finance.trialBalance"))}</h2>
        </div>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t(k("finance.account"))}</th>
                <th>{t("aplPanel.name")}</th>
                <th>{t(k("finance.debit"))}</th>
                <th>{t(k("finance.credit"))}</th>
                <th>{t(k("finance.balance"))}</th>
              </tr>
            </thead>
            <tbody>
              {data.accountBalances.map((row) => (
                <tr key={row.accountId}>
                  <td>{String(row.code)}</td>
                  <td>{String(row.name)}</td>
                  <td>{formatNumber(Number(row.debit))}</td>
                  <td>{formatNumber(Number(row.credit))}</td>
                  <td>{formatNumber(Number(row.balance))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <AgingTable title={t(k("finance.receivables"))} rows={data.receivables} />
      <AgingTable title={t(k("finance.payables"))} rows={data.payables} />
      <section className="data-panel administration-panel">
        <div className="section-heading">
          <h2>{t(k("finance.cashForecast"))}</h2>
        </div>
        <div className="administration-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t(k("finance.valueDate"))}</th>
                <th>{t(k("finance.receipts"))}</th>
                <th>{t(k("finance.payments"))}</th>
                <th>{t(k("finance.net"))}</th>
              </tr>
            </thead>
            <tbody>
              {data.cashForecast.map((row) => (
                <tr key={String(row.date)}>
                  <td>{String(row.date)}</td>
                  <td>{formatNumber(Number(row.receipts))}</td>
                  <td>{formatNumber(Number(row.payments))}</td>
                  <td>{formatNumber(Number(row.net))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
function StatementTable({
  title,
  rows,
}: {
  title: string;
  rows: FinanceRecord[];
}) {
  const { t, formatNumber } = useLocale();
  return (
    <section className="data-panel administration-panel">
      <div className="section-heading">
        <h2>{title}</h2>
      </div>
      <div className="administration-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t(k("finance.account"))}</th>
              <th>{t("aplPanel.name")}</th>
              <th>{t(k("finance.accountType"))}</th>
              <th>{t(k("finance.amount"))}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.accountId}-${index}`}>
                <td>{String(row.code)}</td>
                <td>{String(row.name)}</td>
                <td>
                  {t(k(`finance.accountType.${String(row.accountType)}`))}
                </td>
                <td>{formatNumber(Number(row.amount))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function AgingTable({ title, rows }: { title: string; rows: FinanceRecord[] }) {
  const { t, formatNumber } = useLocale();
  return (
    <section className="data-panel administration-panel">
      <div className="section-heading">
        <h2>{title}</h2>
      </div>
      <div className="administration-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t("quality.number")}</th>
              <th>{t(k("finance.party"))}</th>
              <th>{t(k("purchasing.dueDate"))}</th>
              <th>{t(k("finance.amount"))}</th>
              <th>{t(k("finance.pending"))}</th>
              <th>{t(k("finance.daysOverdue"))}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.sourceId}-${index}`}>
                <td>{String(row.number)}</td>
                <td>{String(row.party)}</td>
                <td>{String(row.dueDate)}</td>
                <td>{formatNumber(Number(row.amount))}</td>
                <td>{formatNumber(Number(row.pending))}</td>
                <td>{String(row.daysOverdue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FinanceEditor({
  section,
  item,
  data,
  cancel,
  saved,
  onPrevious,
  onNext,
}: {
  section: FinanceSection;
  item: FinanceRecord | null;
  data: FinanceSnapshot;
  cancel: () => void;
  saved: () => Promise<void>;
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
}) {
  if (item && ["years", "entries", "treasury"].includes(section))
    return (
      <FinanceRecordDetails
        section={section}
        item={item}
        data={data}
        cancel={cancel}
        onPrevious={onPrevious}
        onNext={onNext}
      />
    );
  if (section === "entries")
    return <EntryEditor data={data} cancel={cancel} saved={saved} />;
  const { t } = useLocale(),
    [error, setError] = useState<string>();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(event.currentTarget),
    );
    for (const name of ["year", "taxRate", "amount"])
      if (body[name]) body[name] = Number(body[name]);
    body.isActive =
      (
        event.currentTarget.elements.namedItem(
          "isActive",
        ) as HTMLInputElement | null
      )?.checked ?? true;
    if (section === "accounts")
      body.allowsPosting =
        (
          event.currentTarget.elements.namedItem(
            "allowsPosting",
          ) as HTMLInputElement | null
        )?.checked ?? false;
    try {
      await saveFinance(entities[section], body, item?.id);
      await saved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "finance.saveError");
    }
  };
  return (
    <RecordLayout
      title={t(k(`finance.${section}`))}
      cancel={cancel}
      form="finance-form"
      onPrevious={onPrevious}
      onNext={onNext}
    >
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
        <form
          id="finance-form"
          className="administration-form record-form"
          onSubmit={submit}
        >
          <div className="compact-form-grid">
          {["accounts", "journals", "taxes", "banks"].includes(section) && (
            <Field
              label={t("aplPanel.code")}
              name="code"
              value={item?.code}
              required
            />
          )}
          {section === "years" && (
            <>
              <Field
                label={t(k("finance.year"))}
                name="year"
                value={item?.year ?? new Date().getFullYear()}
                type="number"
                required
              />
              <DateFields item={item} />
            </>
          )}
          {section === "accounts" && (
            <>
              <Field
                label={t("aplPanel.name")}
                name="name"
                value={item?.name}
                required
              />
              <label>
                <span>{t(k("finance.accountType"))}</span>
                <select
                  name="accountType"
                  defaultValue={String(item?.accountType ?? "Asset")}
                >
                  {["Asset", "Liability", "Equity", "Income", "Expense"].map(
                    (type) => (
                      <option key={type} value={type}>
                        {t(k(`finance.accountType.${type}`))}
                      </option>
                    ),
                  )}
                </select>
              </label>
              <Field
                label={t(k("finance.parentAccount"))}
                name="parentCode"
                value={item?.parentCode}
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="allowsPosting"
                  defaultChecked={item?.allowsPosting !== false}
                />
                <span>{t(k("finance.allowsPosting"))}</span>
              </label>
            </>
          )}
          {section === "journals" && (
            <Field
              label={t("aplPanel.name")}
              name="name"
              value={item?.name}
              required
            />
          )}
          {section === "taxes" && (
            <>
              <Field
                label={t("aplPanel.name")}
                name="name"
                value={item?.name}
                required
              />
              <Field
                label={t(k("finance.taxRate"))}
                name="taxRate"
                value={item?.rate}
                type="number"
                required
              />
              <Field
                label={t(k("finance.outputTaxAccount"))}
                name="outputTaxAccountCode"
                value={item?.outputTaxAccountCode ?? "477000"}
                required
              />
              <Field
                label={t(k("finance.inputTaxAccount"))}
                name="inputTaxAccountCode"
                value={item?.inputTaxAccountCode ?? "472000"}
                required
              />
            </>
          )}
          {section === "banks" && (
            <>
              <Field
                label={t("aplPanel.name")}
                name="name"
                value={item?.name}
                required
              />
              <Field
                label={t(k("finance.iban"))}
                name="iban"
                value={item?.iban}
              />
              <Field
                label={t(k("finance.ledgerAccount"))}
                name="ledgerAccountCode"
                value={item?.ledgerAccountCode}
                required
              />
              <Field
                label={t(k("finance.currency"))}
                name="currencyCode"
                value={item?.currencyCode ?? "EUR"}
                required
              />
            </>
          )}
          {section === "treasury" && <TreasuryFields item={item} data={data} />}{" "}
          {section !== "years" && section !== "treasury" && (
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                defaultChecked={item?.isActive !== false}
              />
              <span>{t("administration.active")}</span>
            </label>
          )}
          </div>
        </form>
    </RecordLayout>
  );
}

function FinanceRecordDetails({
  section,
  item,
  data,
  cancel,
  onPrevious,
  onNext,
}: {
  section: FinanceSection;
  item: FinanceRecord;
  data: FinanceSnapshot;
  cancel: () => void;
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
}) {
  const { t, formatNumber } = useLocale();
  const lines =
    section === "entries"
      ? data.entryLines.filter((line) => line.accountingEntryId === item.id)
      : [];
  return (
    <RecordLayout
      title={String(item.number ?? item.year ?? t(k(`finance.${section}`)))}
      cancel={cancel}
      form=""
      onPrevious={onPrevious}
      onNext={onNext}
    >
      <section className="data-panel administration-panel">
        <dl className="record-detail-grid">
          <div>
            <dt>{t("planning.status")}</dt>
            <dd>
              {String(
                item.status ??
                  (item.isClosed
                    ? t(k("finance.closed"))
                    : t("administration.active")),
              )}
            </dd>
          </div>
          <div>
            <dt>{t("administration.description")}</dt>
            <dd>{String(item.description ?? item.name ?? "—")}</dd>
          </div>
          <div>
            <dt>{t(k("commercial.total"))}</dt>
            <dd>
              {typeof (item.amount ?? item.debitTotal) === "number"
                ? formatNumber(Number(item.amount ?? item.debitTotal))
                : "—"}
            </dd>
          </div>
        </dl>
        {lines.length > 0 && (
          <div className="administration-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t(k("finance.account"))}</th>
                  <th>{t("administration.description")}</th>
                  <th>{t(k("finance.debit"))}</th>
                  <th>{t(k("finance.credit"))}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id}>
                    <td>
                      {String(
                        data.accounts.find(
                          (account) =>
                            account.id === line.generalLedgerAccountId,
                        )?.code ?? "—",
                      )}
                    </td>
                    <td>{String(line.description ?? "—")}</td>
                    <td>{formatNumber(Number(line.debit ?? 0))}</td>
                    <td>{formatNumber(Number(line.credit ?? 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </RecordLayout>
  );
}

function TreasuryFields({
  item,
  data,
}: {
  item: FinanceRecord | null;
  data: FinanceSnapshot;
}) {
  const { t } = useLocale();
  return (
    <>
      <Field
        label={t("quality.number")}
        name="number"
        value={item?.number}
        required
      />
      <label>
        <span>{t(k("finance.transactionKind"))}</span>
        <select
          name="transactionKind"
          defaultValue={String(item?.kind ?? "CustomerReceipt")}
        >
          <option value="CustomerReceipt">{t(k("finance.receipt"))}</option>
          <option value="SupplierPayment">{t(k("finance.payment"))}</option>
          <option value="Transfer">{t(k("finance.transfer"))}</option>
          <option value="Adjustment">{t(k("finance.adjustment"))}</option>
        </select>
      </label>
      <Field
        label={t(k("finance.valueDate"))}
        name="valueDate"
        value={item?.valueDate}
        type="date"
        required
      />
      <Field
        label={t(k("finance.amount"))}
        name="amount"
        value={item?.amount}
        type="number"
        required
      />
      <label>
        <span>{t(k("finance.bankAccount"))}</span>
        <select
          name="bankAccountId"
          defaultValue={String(item?.financialBankAccountId ?? "")}
        >
          <option value="">—</option>
          {data.bankAccounts.map((bank) => (
            <option key={bank.id} value={bank.id}>
              {String(bank.code)} · {String(bank.name)}
            </option>
          ))}
        </select>
      </label>
      <Field
        label={t(k("finance.currency"))}
        name="currencyCode"
        value={item?.currencyCode ?? "EUR"}
        required
      />
    </>
  );
}

function EntryEditor({
  data,
  cancel,
  saved,
}: {
  data: FinanceSnapshot;
  cancel: () => void;
  saved: () => Promise<void>;
}) {
  const { t, formatNumber } = useLocale(),
    [error, setError] = useState<string>(),
    [lines, setLines] = useState<EntryLine[]>([blankLine(), blankLine()]);
  const change = (
    index: number,
    name: keyof EntryLine,
    value: string | number,
  ) =>
    setLines((current) =>
      current.map((line, position) =>
        position === index ? { ...line, [name]: value } : line,
      ),
    );
  const debit = lines.reduce((sum, line) => sum + Number(line.debit || 0), 0),
    credit = lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await saveFinance("entries", { ...body, lines });
      await saved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "finance.saveError");
    }
  };
  return (
    <RecordLayout
      title={t(k("finance.entries"))}
      cancel={cancel}
      form="finance-entry-form"
    >
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
      <section className="data-panel administration-panel">
        <form
          id="finance-entry-form"
          className="administration-form record-form"
          onSubmit={submit}
        >
          <div className="compact-form-grid">
          <Field label={t("quality.number")} name="number" required />
          <Field
            label={t(k("finance.entryDate"))}
            name="entryDate"
            type="date"
            required
          />
          <label>
            <span>{t(k("finance.period"))}</span>
            <select name="accountingPeriodId" required>
              <option value="">—</option>
              {data.periods
                .filter((period) => period.status === "Open")
                .map((period) => (
                  <option key={period.id} value={period.id}>
                    {String(period.name)} · {String(period.startsOn)}–
                    {String(period.endsOn)}
                  </option>
                ))}
            </select>
          </label>
          <label>
            <span>{t(k("finance.journal"))}</span>
            <select name="accountingJournalId" required>
              <option value="">—</option>
              {data.journals
                .filter((journal) => journal.isActive !== false)
                .map((journal) => (
                  <option key={journal.id} value={journal.id}>
                    {String(journal.code)} · {String(journal.name)}
                  </option>
                ))}
            </select>
          </label>
          <label className="form-field-wide">
            <span>{t("administration.description")}</span>
            <textarea name="description" required />
          </label>
          </div>
        </form>
        <div className="section-heading">
          <h2>{t(k("finance.entryLines"))}</h2>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setLines((current) => [...current, blankLine()])}
          >
            + {t("administration.add")}
          </button>
        </div>
        {lines.map((line, index) => (
          <div className="administration-form inventory-inline-form compact-form-grid finance-entry-line" key={index}>
            <label>
              <span>{t(k("finance.account"))}</span>
              <select
                value={line.accountId}
                onChange={(event) =>
                  change(index, "accountId", event.target.value)
                }
                required
              >
                <option value="">—</option>
                {data.accounts
                  .filter(
                    (account) =>
                      account.isActive !== false &&
                      account.allowsPosting !== false,
                  )
                  .map((account) => (
                    <option key={account.id} value={account.id}>
                      {String(account.code)} · {String(account.name)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              <span>{t(k("finance.debit"))}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={line.debit}
                onChange={(event) =>
                  change(index, "debit", Number(event.target.value))
                }
              />
            </label>
            <label>
              <span>{t(k("finance.credit"))}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={line.credit}
                onChange={(event) =>
                  change(index, "credit", Number(event.target.value))
                }
              />
            </label>
            <label>
              <span>{t("administration.description")}</span>
              <input
                value={line.description}
                onChange={(event) =>
                  change(index, "description", event.target.value)
                }
              />
            </label>
            <button
              className="danger-button"
              type="button"
              disabled={lines.length <= 2}
              onClick={() =>
                setLines((current) =>
                  current.filter((_, position) => position !== index),
                )
              }
              aria-label={t(k("finance.removeLine"))}
            >
              ×
            </button>
          </div>
        ))}
        <p
          className={
            debit === credit && debit > 0 ? "status-success" : "form-error"
          }
        >
          {t(k("finance.debit"))}: {formatNumber(debit)} ·{" "}
          {t(k("finance.credit"))}: {formatNumber(credit)}
        </p>
      </section>
    </RecordLayout>
  );
}

function RecordLayout({
  title,
  cancel,
  form,
  onPrevious,
  onNext,
  children,
}: {
  title: string;
  cancel: () => void;
  form?: string;
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
  children: ReactNode;
}) {
  const { t } = useLocale();
  return (
    <main className="page-content"><AppModal title={title} closeLabel={t("recordNavigation.backToList")} onClose={cancel} onPrevious={onPrevious} onNext={onNext} saveForm={Boolean(form)} wide>{children}</AppModal></main>
  );
}
function Field({
  label,
  name,
  value,
  type = "text",
  required = false,
}: {
  label: string;
  name: string;
  value?: unknown;
  type?: string;
  required?: boolean;
}) {
  return (
    <label>
      <span>{label}</span>
      <input
        name={name}
        type={type}
        defaultValue={value == null ? "" : String(value)}
        required={required}
        step={type === "number" ? "any" : undefined}
      />
    </label>
  );
}
function DateFields({ item }: { item: FinanceRecord | null }) {
  const { t } = useLocale();
  return (
    <>
      <Field
        label={t(k("finance.startsOn"))}
        name="startsOn"
        value={item?.startsOn}
        type="date"
        required
      />
      <Field
        label={t(k("finance.endsOn"))}
        name="endsOn"
        value={item?.endsOn}
        type="date"
        required
      />
    </>
  );
}
function route(section: FinanceSection) {
  return (
    {
      accounts: "plan-contable",
      years: "ejercicios",
      journals: "diarios",
      entries: "asientos",
      taxes: "impuestos",
      treasury: "tesoreria",
      banks: "bancos",
      sii: "sii",
      reports: "informes",
    } as Record<string, string>
  )[section];
}
